import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import i18n from "../lib/i18n";
import type { StadiumAuditPlannerData } from "../types/stadiumAudit.types";

/** Root of the planner key; prefix-invalidated by realtime on mission_orders / mission_order_batches changes. */
export const STADIUM_AUDIT_QUERY_KEY = ["stadium-audit-planner"] as const;

const fetchPlannerData = async (
    leagueId: string | null,
    season?: string | null,
    missionsOnly = false
): Promise<StadiumAuditPlannerData> => {
    if (!leagueId) {
        throw new Error(i18n.t("stadiums:planner.no_league_selected"));
    }
    const { data, error } = await supabase.functions.invoke("stadium-audit-planner", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: { action: "list", leagueId, season: season ?? null, missionsOnly },
    });
    if (error) throw error;
    return data as StadiumAuditPlannerData;
};

export function useStadiumAuditPlanner(
    leagueId: string | null,
    season?: string | null,
    options?: {
        enabled?: boolean;
        /**
         * Accounting kanban: `missions` only (officials, stadiums, groups, teams and
         * auditHistory come back empty). Own key, under the same [root, leagueId]
         * prefix so the planner's invalidations and optimistic updates reach it.
         */
        missionsOnly?: boolean;
    }
) {
    const missionsOnly = options?.missionsOnly === true;
    return useQuery({
        queryKey: [...STADIUM_AUDIT_QUERY_KEY, leagueId, season ?? null, ...(missionsOnly ? ["missions-only"] : [])],
        queryFn: () => fetchPlannerData(leagueId, season, missionsOnly),
        enabled: !!leagueId && options?.enabled !== false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
}
